import { EnvironmentProviders, makeEnvironmentProviders, inject, ENVIRONMENT_INITIALIZER } from '@angular/core';
import { UnitsDistributionRegistry } from './services/units-distribution.registry';
import { CorrectScoreComponent } from './types/correct-score.component';
import { SepDefaultComponent } from './types/sep-default.component';
import { AsianHandicapComponent } from './types/asian-handicap.component';
import { HdaComponent } from './types/hda.component';
import { OverUnderComponent } from './types/over-under.component';
import { BothToScoreComponent } from './types/both-to-score.component';
import { H2hComponent } from './types/h2h.component';
import { TennisCorrectScoreComponent } from './types/tennis-correct-score.component';
import { PointMarginComponent } from './types/point-margin.component';
import { NbaPointMarginComponent } from './types/nba-point-margin.component';
import { WrcComponent } from './types/wrc.component';
import { HorseRacingComponent } from './types/horse-racing.component';
import { GreyhoundsComponent } from './types/greyhounds.component';
import { IceHockeyComponent } from './types/ice-hockey.component';
import { DartsComponent } from './types/darts.component';
import { DartsCorrectScoreComponent } from './types/darts-cs.component';
import { GolfComponent } from './types/golf.component';
import { EsportsHandicapComponent } from './types/esports-ha.component';
import { EsportsHdaComponent } from './types/esports-hda.component';
import { CricketDefaultComponent } from './types/cricket-default.component';
import { NflAvbComponent } from './types/nfl-avb.component';
import { FantasyOuComponent } from './types/fantasy-ou.component';
import { FantasyMarginComponent } from './types/fantasy-margin.component';

export function provideUnitsDistributionTemplates(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useFactory: () => {
        const registry = inject(UnitsDistributionRegistry);
        return () => {
          registry.register('FOOTBALL', 'CORRECT_SCORE', CorrectScoreComponent);
          registry.register('FOOTBALL', 'ASIAN_HANDICAP', AsianHandicapComponent);
          registry.register('FOOTBALL', 'HDA', HdaComponent);
          registry.register('FOOTBALL', 'MATCH_RESULT', HdaComponent);
          registry.register('FOOTBALL', 'OVER_UNDER', OverUnderComponent);
          registry.register('FOOTBALL', 'BTTS', BothToScoreComponent);

          registry.register('TENNIS', 'H2H', H2hComponent);
          registry.register('TENNIS', 'CORRECT_SCORE_3', TennisCorrectScoreComponent);
          registry.register('TENNIS', 'CORRECT_SCORE_5', TennisCorrectScoreComponent);

          registry.register('NFL', 'POINT_MARGIN', PointMarginComponent);
          registry.register('NFL', 'AVB', NflAvbComponent);

          registry.register('NBA', 'POINT_MARGIN', NbaPointMarginComponent);

          registry.register('WRC', 'RACE_WIN', WrcComponent);
          registry.register('WRC', 'RACE_ORDER', WrcComponent);

          registry.register('HORSE_RACING', 'RACE_WIN', HorseRacingComponent);
          registry.register('HORSE_RACING', 'RACE_ORDER', HorseRacingComponent);
          registry.register('HORSE_RACING', 'RACE_PLACE', HorseRacingComponent);

          registry.register('GREYHOUNDS', 'RACE_WIN', GreyhoundsComponent);
          registry.register('GREYHOUNDS', 'RACE_ORDER', GreyhoundsComponent);
          registry.register('GREYHOUNDS', 'RACE_PLACE', GreyhoundsComponent);

          registry.register('ICE_HOCKEY', 'POINT_MARGIN', IceHockeyComponent);
          registry.register('ICE_HOCKEY', 'HDA', HdaComponent);

          registry.register('DARTS', 'CORRECT_SCORE_9', DartsCorrectScoreComponent);
          registry.register('DARTS', 'CORRECT_SCORE_12', DartsComponent);
          registry.register('DARTS', 'CORRECT_SCORE_14', DartsComponent);

          registry.register('GOLF', 'EVENT_WIN', GolfComponent);
          registry.register('GOLF', 'EVENT_WIN_2', GolfComponent);
          registry.register('GOLF', 'EVENT_WIN_3', GolfComponent);

          registry.register('ESPORTS', 'HA', EsportsHandicapComponent);
          registry.register('ESPORTS', 'HDA', EsportsHdaComponent);

          registry.register('CRICKET', 'CKT_MATCH_RESULT', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_OP_PARTNERSHIP', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_FST_SCR_SHOT', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_1INN_WICKET', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_2INN_WICKET', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_MATCH_SIXES', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_WICKETS_OU', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_PROP_RUNS_50', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_PROP_WICKETS', CricketDefaultComponent);
          registry.register('CRICKET', 'CKT_PROP_RUNS_20', CricketDefaultComponent);

          registry.register('NFL', 'OVER_UNDER_FANTASY', FantasyOuComponent);
          registry.register('NBA', 'OVER_UNDER_FANTASY', FantasyOuComponent);
          registry.register('ICE_HOCKEY', 'OVER_UNDER_FANTASY', FantasyOuComponent);
          registry.register('BASEBALL', 'OVER_UNDER_FANTASY', FantasyOuComponent);
          registry.register('FOOTBALL', 'OVER_UNDER_FANTASY', FantasyOuComponent);
          registry.register('FANTASY_GAMES', 'OVER_UNDER_FANTASY', FantasyOuComponent);
          registry.register('NCAA_BASKETBALL', 'OVER_UNDER_FANTASY', FantasyOuComponent);

          registry.register('NFL', 'MARGIN_FANTASY', FantasyMarginComponent);
          registry.register('NBA', 'MARGIN_FANTASY', FantasyMarginComponent);
          registry.register('ICE_HOCKEY', 'MARGIN_FANTASY', FantasyMarginComponent);
          registry.register('BASEBALL', 'MARGIN_FANTASY', FantasyMarginComponent);
          registry.register('FOOTBALL', 'MARGIN_FANTASY', FantasyMarginComponent);
          registry.register('FANTASY_GAMES', 'MARGIN_FANTASY', FantasyMarginComponent);
          registry.register('NCAA_BASKETBALL', 'MARGIN_FANTASY', FantasyMarginComponent);

          const sepTypeCodes = [
            'HOME_CORNERS', 'AWAY_CORNERS',
            'HT_FT', 'TOT_GOALS',
            'PENALTY_TAKEN', 'VAR_OVERTURN',
            'RED_CARD', 'RED_OR_PENALTY',
            'TIME_FIRST_GOAL', 'GOAL_FH',
            'HOME_CORNERS_OU', 'AWAY_CORNERS_OU',
            'TOT_CORNERS', 'TOT_CORNERS_OU',
            'FH_CORNERS', 'SH_CORNERS',
            'HOME_CARDS', 'AWAY_CARDS',
            'TOT_CARDS_OU', 'TOT_BP',
            'HOME_BP', 'AWAY_BP',
            'FH_CARDS', 'SH_CARDS',
            'OVER_UNDER'
          ];

          sepTypeCodes.forEach(typeCode => {
            registry.register('FOOTBALL', typeCode, SepDefaultComponent);
          });

        };
      }
    }
  ]);
}
